import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { Layout } from '../Layout';
import { ScrollIndicator } from '../ScrollIndicator';
import { PageTransition } from '../PageTransition';
import { env } from '@/config/env';
import { ErrorBoundary } from '../ErrorBoundary';

// The admin panel shares nothing with the public site, and nobody who visits a
// marketing page needs it. Keeping it out of the entry chunk is a win for both:
// the site drops the panel, the panel drops the marketing pages and their images.
const AdminPanel = lazy(() => import('../admin/AdminPanel').then(m => ({ default: m.AdminPanel })));

// Paid search lands on /for-employers. Fetch the home page only when it is
// visited, so its consulting form and sections do not delay QA hiring traffic.
const Home = lazy(() => import('../pages/Home').then(m => ({ default: m.Home })));

const ForQAEngineers = lazy(() => import('../pages/ForQAEngineers').then(m => ({ default: m.ForQAEngineers })));
const ForEmployers = lazy(() => import('../pages/ForEmployers').then(m => ({ default: m.ForEmployers })));
const ApplyQAEngineer = lazy(() => import('../pages/ApplyQAEngineer').then(m => ({ default: m.ApplyQAEngineer })));
const HireQATalent = lazy(() => import('../pages/HireQATalent').then(m => ({ default: m.HireQATalent })));
const Blog = lazy(() => import('../pages/Blog').then(m => ({ default: m.Blog })));
const ArticleDetail = lazy(() => import('../pages/ArticleDetail').then(m => ({ default: m.ArticleDetail })));
const About = lazy(() => import('../pages/About').then(m => ({ default: m.About })));
const Contact = lazy(() => import('../pages/Contact').then(m => ({ default: m.Contact })));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('../pages/TermsOfService').then(m => ({ default: m.TermsOfService })));
const AITestingUseCases = lazy(() => import('../pages/AITestingUseCases').then(m => ({ default: m.AITestingUseCases })));
const AiDrivenValidation = lazy(() => import('../pages/services/AiDrivenValidation').then(m => ({ default: m.AiDrivenValidation })));
const QaArchitectureEngineering = lazy(() => import('../pages/services/QaArchitectureEngineering').then(m => ({ default: m.QaArchitectureEngineering })));
const QaConsultingModernization = lazy(() => import('../pages/services/QaConsultingModernization').then(m => ({ default: m.QaConsultingModernization })));
const IntelligentQualityOperations = lazy(() => import('../pages/services/IntelligentQualityOperations').then(m => ({ default: m.IntelligentQualityOperations })));
const IndustriesHub = lazy(() => import('../pages/industries/IndustriesHub').then(m => ({ default: m.IndustriesHub })));
const IndustryFintech = lazy(() => import('../pages/industries/IndustryFintech').then(m => ({ default: m.IndustryFintech })));
const IndustryHealthcare = lazy(() => import('../pages/industries/IndustryHealthcare').then(m => ({ default: m.IndustryHealthcare })));
const IndustryPharma = lazy(() => import('../pages/industries/IndustryPharma').then(m => ({ default: m.IndustryPharma })));
const IndustryEcommerce = lazy(() => import('../pages/industries/IndustryEcommerce').then(m => ({ default: m.IndustryEcommerce })));
const IndustryCommunications = lazy(() => import('../pages/industries/IndustryCommunications').then(m => ({ default: m.IndustryCommunications })));
const CaseStudiesHub = lazy(() => import('../pages/case-studies/CaseStudiesHub').then(m => ({ default: m.CaseStudiesHub })));
const CaseAiChatbotTesting = lazy(() => import('../pages/case-studies/CaseAiChatbotTesting').then(m => ({ default: m.CaseAiChatbotTesting })));
const CaseOnDeviceAiTesting = lazy(() => import('../pages/case-studies/CaseOnDeviceAiTesting').then(m => ({ default: m.CaseOnDeviceAiTesting })));
const CaseAiTestGeneration = lazy(() => import('../pages/case-studies/CaseAiTestGeneration').then(m => ({ default: m.CaseAiTestGeneration })));
/*
 * A developer scratch page. It was routed unconditionally, so /consent-demo
 * answered on the live marketing site. Mounted only outside production now; in a
 * production build the import is unreachable and Rollup drops the chunk.
 */
/*
 * The AI-Native Engineering deck. Unlisted on purpose: nothing links to it, the
 * page sets robots noindex, and it renders without the site chrome so the slides
 * fill the screen.
 */
const DeckAiNative = lazy(() => import('../pages/DeckAiNative').then(m => ({ default: m.DeckAiNative })));

const NotFound = lazy(() => import('../pages/NotFound').then(m => ({ default: m.NotFound })));

const ConsentCheckboxDemo = lazy(() => import('../ConsentCheckboxDemo').then(m => ({ default: m.ConsentCheckboxDemo })));

/**
 * Wrapper component for main website pages with layout and transitions
 */
const MainWebsiteWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const content = (
    <ErrorBoundary resetKey={pathname}>
      <Suspense fallback={<div className="min-h-screen" />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );

  return (
    <Layout>
      {pathname === '/for-employers' ? content : <PageTransition>{content}</PageTransition>}
      <ScrollIndicator />
    </Layout>
  );
};

/**
 * Main application routes component
 * Handles both admin panel and main website routing
 */
export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Admin Panel Routes - No Layout */}
      <Route 
        path="/admin-qa-management-portal" 
        element={<Navigate to="/admin-qa-management-portal/dashboard" replace />} 
      />
      <Route 
        path="/admin-qa-management-portal/*" 
        element={
          /* The panel gets its own boundary: a crash in one admin page should
             not take out the sidebar and strand whoever is signed in. */
          <ErrorBoundary>
            <Suspense fallback={<div className="min-h-screen bg-[#1a2e49]" />}>
              <AdminPanel />
            </Suspense>
          </ErrorBoundary>
        } 
      />
      
      {/* Unlisted presentation viewer - no layout, no navigation entry. */}
      <Route
        path="/deck/ai-native-engineering"
        element={
          <ErrorBoundary>
            <Suspense fallback={<div className="min-h-screen bg-[#070f22]" />}>
              <DeckAiNative />
            </Suspense>
          </ErrorBoundary>
        }
      />

      {/* Main Website Routes - With Layout and Page Transitions */}
      <Route 
        path="/" 
        element={
          <MainWebsiteWrapper>
            <Home />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/for-qa-engineers" 
        element={
          <MainWebsiteWrapper>
            <ForQAEngineers />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/for-employers" 
        element={
          <MainWebsiteWrapper>
            <ForEmployers />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/apply-qa-engineer" 
        element={
          <MainWebsiteWrapper>
            <ApplyQAEngineer />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/hire-qa-talent" 
        element={
          <MainWebsiteWrapper>
            <HireQATalent />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/blog" 
        element={
          <MainWebsiteWrapper>
            <Blog />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/blog/article/:slug" 
        element={
          <MainWebsiteWrapper>
            <ArticleDetail />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/about" 
        element={
          <MainWebsiteWrapper>
            <About />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/contact" 
        element={
          <MainWebsiteWrapper>
            <Contact />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/privacy-policy" 
        element={
          <MainWebsiteWrapper>
            <PrivacyPolicy />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/terms-of-service" 
        element={
          <MainWebsiteWrapper>
            <TermsOfService />
          </MainWebsiteWrapper>
        } 
      />
      {/* Superseded by the /industries hub. Kept as a redirect: the path is
          live and may be linked to from outside the site. */}
      <Route path="/industry" element={<Navigate to="/industries" replace />} />
      <Route 
        path="/ai-testing-use-cases" 
        element={
          <MainWebsiteWrapper>
            <AITestingUseCases />
          </MainWebsiteWrapper>
        } 
      />
      {/* Services */}
      <Route 
        path="/services" 
        element={<Navigate to="/services/ai-driven-validation" replace />} 
      />
      <Route 
        path="/services/ai-driven-validation" 
        element={
          <MainWebsiteWrapper>
            <AiDrivenValidation />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/services/qa-architecture-engineering" 
        element={
          <MainWebsiteWrapper>
            <QaArchitectureEngineering />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/services/qa-consulting-modernization" 
        element={
          <MainWebsiteWrapper>
            <QaConsultingModernization />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/services/intelligent-quality-operations" 
        element={
          <MainWebsiteWrapper>
            <IntelligentQualityOperations />
          </MainWebsiteWrapper>
        } 
      />
      
      {/* Industries */}
      <Route 
        path="/industries" 
        element={
          <MainWebsiteWrapper>
            <IndustriesHub />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/industries/fintech" 
        element={
          <MainWebsiteWrapper>
            <IndustryFintech />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/industries/healthcare" 
        element={
          <MainWebsiteWrapper>
            <IndustryHealthcare />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/industries/pharma" 
        element={
          <MainWebsiteWrapper>
            <IndustryPharma />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/industries/ecommerce" 
        element={
          <MainWebsiteWrapper>
            <IndustryEcommerce />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/industries/communications" 
        element={
          <MainWebsiteWrapper>
            <IndustryCommunications />
          </MainWebsiteWrapper>
        } 
      />

      {/* Case studies */}
      <Route 
        path="/case-studies" 
        element={
          <MainWebsiteWrapper>
            <CaseStudiesHub />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/case-studies/ai-chatbot-testing" 
        element={
          <MainWebsiteWrapper>
            <CaseAiChatbotTesting />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/case-studies/on-device-ai-testing" 
        element={
          <MainWebsiteWrapper>
            <CaseOnDeviceAiTesting />
          </MainWebsiteWrapper>
        } 
      />
      <Route 
        path="/case-studies/ai-test-generation" 
        element={
          <MainWebsiteWrapper>
            <CaseAiTestGeneration />
          </MainWebsiteWrapper>
        } 
      />
      
      {!env.isProduction && (
        <Route
          path="/consent-demo"
          element={
            <MainWebsiteWrapper>
              <ConsentCheckboxDemo />
            </MainWebsiteWrapper>
          }
        />
      )}

      
      {/*
        Catch-all. It used to redirect to "/", which answered 200 for every
        mistyped URL -- a soft 404 to a crawler, and a silent teleport to a
        visitor. nginx serves 404.html with a real 404 status for anything that
        is not a prerendered route; this covers the same paths reached by
        client-side navigation.
      */}
      <Route
        path="*"
        element={
          <MainWebsiteWrapper>
            <NotFound />
          </MainWebsiteWrapper>
        }
      />
    </Routes>
  );
};
